import React, { useState, useEffect } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { connect } from "react-redux";
import { getTerminalAssessmentRanking } from "../../actions/TerminalAssessmentRanking.action";
import { getGradeList } from "../../actions/Grade.action";
import { years } from "../../constants/MonthsAndYears";
import { termArray } from "../../constants/termArray";
import { toast } from "react-toastify";

const TerminalAssessmentRankingForm = ({
  grades,
  getGradeList,
  getTerminalAssessmentRanking,
  publicView = false,
}) => {
  const [formData, setFormData] = useState({
    gradeId: "",
    shiftId: "",
    batchId: "",
    year: new Date().getFullYear(),
    term: "1",
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getGradeList();
  }, [getGradeList]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Reset dependent fields when parent field changes
    if (name === "gradeId") {
      setFormData((prev) => ({
        ...prev,
        shiftId: "",
        batchId: "",
      }));
    } else if (name === "shiftId") {
      setFormData((prev) => ({
        ...prev,
        batchId: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.gradeId) {
      toast.error("Please select a class!");
      return;
    }

    setIsLoading(true);
    try {
      const data = await getTerminalAssessmentRanking({
        gradeId: parseInt(formData.gradeId),
        shiftId: formData.shiftId ? parseInt(formData.shiftId) : null,
        batchId: formData.batchId ? parseInt(formData.batchId) : null,
        year: parseInt(formData.year),
        term: parseInt(formData.term),
      });

      if (data && data.success) {
        toast.success("Ranking data loaded successfully!");

        // Scroll down 100px if this is a public component
        if (publicView) {
          setTimeout(() => {
            window.scrollBy({
              top: 500,
              behavior: "smooth",
            });
          }, 100); // Small delay to ensure toast is visible
        }
      } else {
        toast.error(data?.message || "No data found for the selected criteria");
      }
    } catch (error) {
      toast.error("Failed to load ranking data");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <Card bg="white" text="dark" className={`crd shadow`}>
        <Card.Body>
          <Row>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="gradeId" className="d-block">
                  Class <span className="text-danger">*</span>
                </label>
              </div>
              <Form.Select
                name="gradeId"
                value={formData.gradeId}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Class</option>
                {grades?.map((grade) => (
                  <option key={grade.id} value={grade.id}>
                    {grade.name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="year" className="d-block">
                  Year <span className="text-danger">*</span>
                </label>
              </div>
              <Form.Select
                name="year"
                value={formData.year}
                onChange={handleInputChange}
                required
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="term" className="d-block">
                  Term <span className="text-danger">*</span>
                </label>
              </div>
              <Form.Select
                name="term"
                value={formData.term}
                onChange={handleInputChange}
                required
              >
                {termArray.map((term) => (
                  <option key={term.id} value={term.id}>
                    {term.label}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col md={3} className="py-3 d-none d-md-block">
              <div className="d-flex justify-content-end align-items-end h-100">
                <Button
                  onClick={handleSubmit}
                  variant="primary"
                  type="submit"
                  disabled={isLoading || !formData.gradeId}
                  className="btn_primary"
                >
                  {isLoading ? "Loading..." : "Get Ranking"}
                </Button>
              </div>
            </Col>
          </Row>

          <Row>
            {formData.gradeId !== "" ? (
              <Col md={6} className="py-3">
                <div className="d-flex justify-content-between align-items-center pb-2">
                  <label htmlFor="shiftId" className="d-block">
                    Shift <span className="text-muted">(Optional)</span>
                  </label>
                </div>
                <Form.Select
                  name="shiftId"
                  value={formData.shiftId}
                  onChange={handleInputChange}
                >
                  <option value="">All Shifts</option>
                  {grades
                    ?.filter((g) => g.id === parseInt(formData.gradeId))[0]
                    ?.shifts?.map((shift) => (
                      <option key={shift.id} value={shift.id}>
                        {shift.name}
                      </option>
                    ))}
                </Form.Select>
              </Col>
            ) : (
              <></>
            )}

            {formData.gradeId !== "" && formData.shiftId !== "" ? (
              <Col md={6} className="py-3">
                <div className="d-flex justify-content-between align-items-center pb-2">
                  <label htmlFor="batchId" className="d-block">
                    Batch <span className="text-muted">(Optional)</span>
                  </label>
                </div>
                <Form.Select
                  name="batchId"
                  value={formData.batchId}
                  onChange={handleInputChange}
                >
                  <option value="">All Batches</option>
                  {grades
                    ?.filter((g) => g.id === parseInt(formData.gradeId))[0]
                    ?.batches?.filter(
                      (bt) => bt.shiftId === parseInt(formData.shiftId)
                    )
                    ?.map((batch) => (
                      <option key={batch.id} value={batch.id}>
                        {batch.name}
                      </option>
                    ))}
                </Form.Select>
              </Col>
            ) : (
              <></>
            )}
            <Col md={3} className="py-3 d-block d-md-none">
              <div className="d-flex justify-content-end align-items-end h-100">
                <Button
                  onClick={handleSubmit}
                  variant="primary"
                  type="submit"
                  disabled={isLoading || !formData.gradeId}
                  className="btn_primary"
                >
                  {isLoading ? "Loading..." : "Get Ranking"}
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  grades: state.grade.grade,
});

const mapDispatchToProps = {
  getGradeList,
  getTerminalAssessmentRanking,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TerminalAssessmentRankingForm);
